import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/authService';
import type { LoginCredentials } from '../types/auth';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from './useTranslation';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: user, isLoading } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: authService.getCurrentUser,
    retry: false,
    enabled: authService.isAuthenticated(),
  });

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (response) => {
      authService.setAuthData(response);
      queryClient.setQueryData(['auth', 'user'], response.user);
      notifications.show({
        title: t('notifications.success'),
        message: t('notifications.auth.loginSuccess'),
        color: 'green',
      });
      navigate('/');
    },
    onError: (error) => {
      notifications.show({
        title: t('notifications.error'),
        message: error instanceof Error ? error.message : t('notifications.auth.loginError'),
        color: 'red',
      });
    },
  });

  const logout = () => {
    authService.logout();
    queryClient.clear();
    notifications.show({
      title: t('notifications.success'),
      message: t('notifications.auth.logoutSuccess'),
      color: 'green',
    });
    navigate('/login');
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login: (credentials: LoginCredentials) => loginMutation.mutate(credentials),
    logout,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: false,
  };
}; 